/**
 * StaffManagement Component
 * 직원 관리 UI
 */
import React from 'react';
import {
  UserGroupIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  ShieldCheckIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  CameraIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useStaffManagement } from '../use-cases/useStaffManagement';
import { staffManagementService } from '../services/StaffManagementService';

const StaffManagement: React.FC = () => {
  const {
    filteredStaff,
    loading,
    searchTerm,
    selectedRole,
    showCreateModal,
    showEditModal,
    selectedStaff,
    newStaff,
    stats,
    handleSearchChange,
    handleRoleFilterChange,
    handleCreateStaff,
    handleUpdateStaff,
    handleToggleActive,
    handleDeleteStaff,
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
    updateNewStaff,
  } = useStaffManagement();

  // 편집 폼 상태
  const [editForm, setEditForm] = React.useState({
    name: '',
    email: '',
    phone: '',
    role: 'staff' as 'manager' | 'staff' | 'parttime' | 'facility',
    department: '',
  });

  // 편집 모달이 열릴 때 선택된 직원 정보로 폼 초기화
  React.useEffect(() => {
    if (selectedStaff) {
      setEditForm({
        name: selectedStaff.name || '',
        email: selectedStaff.email || '',
        phone: selectedStaff.phone || '',
        role: (selectedStaff.role as 'manager' | 'staff' | 'parttime' | 'facility') || 'staff',
        department: selectedStaff.department || '',
      });
    }
  }, [selectedStaff]);

  const handleEditSubmit = async () => {
    if (selectedStaff) {
      await handleUpdateStaff(selectedStaff.id, editForm);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <UserGroupIcon className="h-8 w-8 mr-3 text-primary-600" />
          직원 관리
        </h1>
        <p className="text-gray-600 mt-1">
          독서실 운영을 함께하는 직원들의 계정과 권한을 관리합니다
        </p>
      </div>

      {/* 상단 액션 바 */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* 검색 */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="이름 또는 이메일로 검색..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* 역할 필터 */}
          <select
            value={selectedRole}
            onChange={(e) => handleRoleFilterChange(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">전체 직원</option>
            <option value="manager">매니저</option>
            <option value="staff">정직원</option>
            <option value="parttime">파트타임</option>
            <option value="facility">시설관리</option>
          </select>

          {/* 직원 추가 버튼 */}
          <button
            onClick={openCreateModal}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            직원 추가
          </button>
        </div>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">전체 직원</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}명</p>
            </div>
            <UserGroupIcon className="h-12 w-12 text-blue-100" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">활성 계정</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{stats.active}명</p>
            </div>
            <CheckCircleIcon className="h-12 w-12 text-green-100" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">매니저</p>
              <p className="text-2xl font-bold text-purple-600 mt-1">{stats.managers}명</p>
            </div>
            <ShieldCheckIcon className="h-12 w-12 text-purple-100" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">파트타임</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.parttime}명</p>
            </div>
            <ClockIcon className="h-12 w-12 text-gray-100" />
          </div>
        </div>
      </div>

      {/* 직원 목록 */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                직원 정보
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                연락처
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                역할
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                상태
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                얼굴 등록
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                마지막 로그인
              </th>
              <th className="relative px-6 py-3">
                <span className="sr-only">액션</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredStaff.map((staff) => (
              <tr key={staff.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                      <UserGroupIcon className="h-6 w-6 text-gray-500" />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{staff.name}</div>
                      <div className="text-sm text-gray-500">{staff.department}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{staff.email}</div>
                  <div className="text-sm text-gray-500">
                    {staffManagementService.formatPhoneNumber(staff.phone)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${staffManagementService.getRoleColorClass(staff.role)}`}
                  >
                    {staffManagementService.getRoleLabel(staff.role)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${staffManagementService.getActiveStatusClass(staff.isActive)}`}
                  >
                    {staffManagementService.getActiveStatusLabel(staff.isActive)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      staff.faceRegistered
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {staff.faceRegistered ? '등록완료' : '미등록'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {staffManagementService.formatDateTime(staff.lastLogin)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {staff.role === 'owner' ? (
                    <span className="text-xs text-gray-400">계정 주인</span>
                  ) : (
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => openEditModal(staff)}
                      className="text-blue-600 hover:text-blue-900"
                      title="수정"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleToggleActive(staff)}
                      className={
                        staff.isActive
                          ? 'text-gray-500 hover:text-gray-700'
                          : 'text-green-600 hover:text-green-900'
                      }
                      title={staff.isActive ? '비활성화' : '활성화'}
                    >
                      {staff.isActive ? (
                        <XCircleIcon className="h-5 w-5" />
                      ) : (
                        <CheckCircleIcon className="h-5 w-5" />
                      )}
                    </button>
                    <button
                      onClick={() => handleDeleteStaff(staff)}
                      className="text-red-600 hover:text-red-900"
                      title="삭제"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredStaff.length === 0 && (
          <div className="text-center py-12">
            <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchTerm || selectedRole !== 'all'
                ? '검색 결과가 없습니다.'
                : '등록된 직원이 없습니다.'}
            </p>
            {!searchTerm && selectedRole === 'all' && (
              <p className="text-sm text-gray-400 mt-2">
                상단의 "직원 추가" 버튼을 눌러 직원을 등록하세요.
              </p>
            )}
          </div>
        )}
      </div>

      {/* 직원 추가 모달 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">새 직원 추가</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  이름 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newStaff.name}
                  onChange={(e) => updateNewStaff({ name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="직원 이름"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  연락처 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={newStaff.phone}
                  onChange={(e) => updateNewStaff({ phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="010-1234-5678"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  이메일 <span className="text-xs text-gray-400">(선택)</span>
                </label>
                <input
                  type="email"
                  value={newStaff.email}
                  onChange={(e) => updateNewStaff({ email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="email@example.com (선택)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  역할 <span className="text-red-500">*</span>
                </label>
                <select
                  value={newStaff.role}
                  onChange={(e) => updateNewStaff({ role: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="manager">매니저</option>
                  <option value="staff">정직원</option>
                  <option value="parttime">파트타임</option>
                  <option value="facility">시설관리</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  부서/담당
                </label>
                <input
                  type="text"
                  value={newStaff.department}
                  onChange={(e) => updateNewStaff({ department: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="운영팀, 야간근무 등"
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={closeCreateModal}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                취소
              </button>
              <button
                onClick={handleCreateStaff}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                직원 추가
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 직원 수정 모달 */}
      {showEditModal && selectedStaff && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">직원 정보 수정</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  이름 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="직원 이름"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  연락처 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="010-1234-5678"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  이메일 <span className="text-xs text-gray-400">(선택)</span>
                </label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="email@example.com (선택)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  역할 <span className="text-red-500">*</span>
                </label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="manager">매니저</option>
                  <option value="staff">정직원</option>
                  <option value="parttime">파트타임</option>
                  <option value="facility">시설관리</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  부서/담당
                </label>
                <input
                  type="text"
                  value={editForm.department}
                  onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="운영팀, 야간근무 등"
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={closeEditModal}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                취소
              </button>
              <button
                onClick={handleEditSubmit}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 얼굴 인식 등록 안내 */}
      <div className="mt-8 bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start">
          <CameraIcon className="h-6 w-6 text-purple-600 mr-3 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-purple-900 mb-2">📱 얼굴 인식 등록 방법</h3>
            <div className="text-sm text-purple-800 space-y-1">
              <p>직원의 얼굴 인식 등록은 <strong>태블릿 앱</strong>에서 진행합니다:</p>
              <ol className="list-decimal list-inside ml-2 space-y-1 mt-2">
                <li>태블릿 앱 실행 후 메인 메뉴로 이동</li>
                <li>"얼굴 등록" 메뉴 선택</li>
                <li>직원 이름 또는 전화번호로 검색</li>
                <li>얼굴 촬영 (정면, 좌측, 우측 등 다양한 각도)</li>
                <li>등록 완료 후 출입 게이트에서 사용 가능</li>
              </ol>
              <p className="mt-3 text-purple-700 bg-purple-100 rounded px-2 py-1">
                💡 <strong>팁:</strong> 여러 각도에서 촬영할수록 인식률이 높아집니다 (최소 5장 권장)
              </p>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default StaffManagement;
